import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  CheckCircle2, XCircle, HelpCircle, RefreshCw, 
  Trophy, Loader2, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizComponent = ({ quizId, lessonTitle, onComplete }) => {
  const { user, token } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/training/quizzes/${quizId}`, { headers });
      setQuiz(response.data);
      
      // Check if already passed
      if (response.data.passed) {
        // Get the last attempt results
        if (response.data.attempts?.length > 0) {
          const lastAttempt = response.data.attempts[0];
          setShowResults(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please log in to take the quiz');
      return;
    }

    // Check if all questions answered
    const answeredCount = Object.keys(selectedAnswers).length;
    if (answeredCount < quiz.questions.length) {
      toast.error(`Please answer all questions (${answeredCount}/${quiz.questions.length})`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/training/quizzes/${quizId}/submit`,
        { answers: selectedAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(response.data);
      setShowResults(true);
      
      if (response.data.passed) {
        toast.success('🎉 Congratulations! You passed the quiz!');
        if (onComplete) onComplete(response.data);
      } else {
        toast.error(`You scored ${response.data.score_percent}%. Need ${response.data.passing_score}% to pass.`);
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setSelectedAnswers({});
    setResults(null);
    setShowResults(false);
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!quiz) return null;

  // Results View
  if (showResults && results) {
    return (
      <Card className={`border-2 ${results.passed ? 'border-green-500 bg-green-50' : 'border-red-300 bg-red-50'}`}>
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            {results.passed ? (
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Trophy className="w-10 h-10 text-green-600" />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
            )}
          </div>
          <CardTitle className="font-heading text-2xl">
            {results.passed ? '🎉 Quiz Passed!' : 'Try Again'}
          </CardTitle>
          <p className="text-slate-600">
            You scored {results.score_percent}% ({results.correct_count}/{results.total_questions} correct)
          </p>
          <p className="text-sm text-slate-500">
            Passing score: {results.passing_score}%
          </p>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {/* Progress bar */}
          <div>
            <Progress 
              value={results.score_percent} 
              className={`h-3 ${results.passed ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`} 
            />
          </div>

          {/* Results breakdown */}
          <div className="space-y-4">
            {results.results.map((r, index) => (
              <div 
                key={r.question_id}
                className={`p-4 rounded-lg ${r.is_correct ? 'bg-green-100' : 'bg-white border border-red-200'}`}
              >
                <div className="flex items-start gap-3">
                  {r.is_correct ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 mb-2">
                      Q{index + 1}: {r.question_text}
                    </p>
                    <div className="space-y-1">
                      {r.options.map((opt, i) => (
                        <div 
                          key={i}
                          className={`text-sm p-2 rounded ${
                            i === r.correct_answer 
                              ? 'bg-green-200 text-green-900 font-medium' 
                              : i === r.user_answer && !r.is_correct
                              ? 'bg-red-200 text-red-900'
                              : 'text-slate-600'
                          }`}
                        >
                          {String.fromCharCode(65 + i)}) {opt}
                          {i === r.correct_answer && ' ✓'}
                          {i === r.user_answer && i !== r.correct_answer && ' (Your answer)'}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 pt-4">
            {!results.passed && (
              <Button onClick={handleRetake} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake Quiz
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Quiz View
  return (
    <Card className="border-slate-200 border-2 border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Quick Quiz
          </CardTitle>
          <Badge variant="outline">
            {Object.keys(selectedAnswers).length}/{quiz.questions?.length || 0} answered
          </Badge>
        </div>
        <p className="text-sm text-slate-600">
          Pass this quiz to complete the lesson. You need {quiz.passing_score || 70}% to pass.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {quiz.questions?.map((question, qIndex) => (
          <div key={question.id} className="p-4 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900 mb-4">
              <span className="text-primary">Q{qIndex + 1}:</span> {question.question_text}
            </p>
            <div className="space-y-2">
              {question.options.map((option, oIndex) => (
                <label
                  key={oIndex}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedAnswers[question.id] === oIndex
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-white border-2 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={selectedAnswers[question.id] === oIndex}
                    onChange={() => handleAnswerSelect(question.id, oIndex)}
                    className="w-4 h-4 text-primary"
                  />
                  <span className="text-sm">
                    <span className="font-medium">{String.fromCharCode(65 + oIndex)})</span> {option}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Submit Button */}
        <div className="flex justify-center pt-4">
          {user ? (
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              size="lg"
              className="w-full max-w-md"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-5 h-5 mr-2" />
              )}
              Submit Quiz
            </Button>
          ) : (
            <div className="text-center">
              <p className="text-slate-600 mb-2">Log in to take the quiz</p>
              <Button onClick={() => window.location.href = '/login'}>
                Log In
              </Button>
            </div>
          )}
        </div>

        {/* Previous attempts */}
        {quiz.attempts?.length > 0 && (
          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600 mb-2">Previous Attempts:</p>
            <div className="flex flex-wrap gap-2">
              {quiz.attempts.slice(0, 5).map((attempt, i) => (
                <Badge 
                  key={i}
                  variant={attempt.passed ? 'default' : 'outline'}
                  className={attempt.passed ? 'bg-green-100 text-green-800' : ''}
                >
                  {attempt.score_percent}% - {attempt.passed ? 'Passed' : 'Failed'}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizComponent;
